import { Box, IconButton, Typography, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon, Print as PrintIcon,
  Description as WordIcon, Assessment as ReportIcon,
} from '@mui/icons-material';
import { REPORT_CSS, reportCardHTML } from './reportCardHtml';

// Shared bulk-report preview overlay.
//
// Full-screen scroll of every report card in a list — each rendered
// via the same reportCardHTML() builder the single-student preview
// uses, so the on-screen scroll is byte-for-byte what would come out
// of the printer.
//
// Consumed by both the teacher-portal ExamReportDialog and the admin
// StudentReportPage. Each card has a hover-anchored toolbar: print
// this one, download this one as Word. The top bar carries the
// single "Print all" button — printing is always an explicit action,
// never a side effect of clicking Show All.
export default function BulkReportPreview({
  list, subtitle, title = 'Class Report Preview',
  onClose, onPrintAll, onPrintOne, onWordOne,
}) {
  const theme = useTheme();
  if (!list) return null;
  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: (t) => t.zIndex.modal + 10,
      bgcolor: 'rgba(15,23,42,0.6)',
      display: 'flex', flexDirection: 'column',
    }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: '#fff', px: { xs: 1.5, md: 3 }, py: 1.2,
        display: 'flex', alignItems: 'center', gap: 1.4, flexWrap: 'wrap',
      }}>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
        <ReportIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, lineHeight: 1.15, fontSize: { xs: 15, md: 17 } }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ opacity: 0.92, display: 'block', lineHeight: 1.15 }}>
              {subtitle} · {list.length} student{list.length === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
        <Button variant="contained" color="inherit" startIcon={<PrintIcon />}
          onClick={onPrintAll}
          sx={{
            bgcolor: 'rgba(255,255,255,0.92)', color: 'primary.main',
            textTransform: 'none', fontWeight: 700,
            '&:hover': { bgcolor: '#fff' },
          }}>
          Print all {list.length}
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <style>{REPORT_CSS}</style>
        <Stack spacing={3} alignItems="center">
          {list.map((d, i) => (
            <Box key={d.student?.index_no || i} sx={{ position: 'relative' }}>
              <Box sx={{
                position: 'absolute', top: 6, right: 6,
                display: 'flex', gap: 0.6, zIndex: 3,
                bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 99,
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              }}>
                <IconButton size="small" title={`Print ${d.student?.fullname || ''}`}
                  onClick={() => onPrintOne(d)}>
                  <PrintIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" title="Download Word"
                  onClick={() => onWordOne(d)}>
                  <WordIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ bgcolor: '#fff', boxShadow: '0 6px 24px rgba(0,0,0,0.25)' }}>
                <div dangerouslySetInnerHTML={{ __html: reportCardHTML(d) }} />
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
